import { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../store/authSlice";
import { showSnackbar } from "../../store/snackbarSlice";
import { useNavigate } from "react-router-dom";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { registerUser } from "../../api/api";

import "./register.css";

const Register = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<any>({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 🔥 HANDLE CHANGE
  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      [key]: key === "email" ? value.trim().toLowerCase() : value,
    }));

    setErrors((prev: any) => ({ ...prev, [key]: "" }));
  };

  // 🔥 VALIDATION
  const validate = () => {
    const newErrors: any = {};

    if (!form.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!form.password) {
      newErrors.password = "Password is required";
    } else if (form.password.length < 6) {
      newErrors.password = "Minimum 6 characters required";
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔥 SUBMIT
  const handleRegister = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      const res = await registerUser({
        name: form.name,
        email: form.email,
        password: form.password,
      });

      const { token, user } = res.data;

      dispatch(
        showSnackbar({
          message:
            "Registration successful 🎉 Please contact support to activate your account.",
          type: "success",
        }),
      );

      // 👉 redirect to login
      navigate("/");
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message:
            err?.message || err?.response?.data?.msg || "Register Failed",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    form.name && form.email && form.password && form.confirmPassword;

  return (
    <Box className="register-container">
      <Paper elevation={0} className="register-card">
        <Typography variant="h5" className="register-title">
          Create Account 🚀
        </Typography>

        <Typography className="register-subtitle">
          Sign up to start using DSA Panel
        </Typography>

        {/* NAME */}
        <TextField
          placeholder="Full Name"
          fullWidth
          size="small"
          className="register-input"
          value={form.name}
          error={!!errors.name}
          helperText={errors.name}
          onChange={(e) => handleChange("name", e.target.value)}
        />

        {/* EMAIL */}
        <TextField
          placeholder="Email Address"
          fullWidth
          size="small"
          className="register-input"
          value={form.email}
          error={!!errors.email}
          helperText={errors.email}
          onChange={(e) => handleChange("email", e.target.value)}
        />

        {/* PASSWORD */}
        <TextField
          placeholder="Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          size="small"
          className="register-input"
          value={form.password}
          error={!!errors.password}
          helperText={errors.password}
          onChange={(e) => handleChange("password", e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {/* CONFIRM PASSWORD */}
        <TextField
          placeholder="Confirm Password"
          type="password"
          fullWidth
          size="small"
          className="register-input"
          value={form.confirmPassword}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          onChange={(e) => handleChange("confirmPassword", e.target.value)}
        />

        {/* BUTTON */}
        <Button
          fullWidth
          className="register-btn"
          onClick={handleRegister}
          disabled={loading || !isFormValid}
        >
          {loading ? "Creating..." : "Create Account"}
        </Button>

        {/* LINKS */}
        <Box className="register-links">
          <Typography className="register-link" onClick={() => navigate("/")}>
            Already have an account?
          </Typography>

          <Typography className="register-link" onClick={() => navigate("/")}>
            Login
          </Typography>
        </Box>

        {/* FOOTER */}
        <Box className="register-footer">
          <Typography>
            Powered by{" "}
            <a
              href="https://ssquarelogic.com/"
              target="_blank"
              className="register-footer-link"
            >
              SSQUARE LOGIC
            </a>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Register;
